import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  useTheme,
  Tabs,
  Tab,
  IconButton,
  Drawer,
  AppBar,
  Toolbar,
  Badge,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  Search as SearchIcon,
  ShoppingCart as CartIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  Receipt as ReceiptIcon,
  FileDownload as DownloadIcon,
  History as HistoryIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  List as ListIcon,
  LocationOn as LocationOnIcon,
  Description as DescriptionIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { styled } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import productService from '../../services/productService';
import salesService from '../../services/salesService';
import { customerService } from '../../services/customerService';
import { getCurrentUser } from '../../services/authService';
import POSProductList from './POSProductList';
import POSCart from './POSCart';
import POSPayment from './POSPayment';
import CustomerSelector from './CustomerSelector';
import creditService from '../../services/creditService';
import categoryService from '../../services/categoryService';
import HoldBillModal from './HoldBillModal';
import ViewHoldBillsModal from './ViewHoldBillsModal';
import { holdBillService } from '../../services/holdBillService';
import companySettingsService from '../../services/companySettingsService';
import printSettingsService from '../../services/printSettingsService';
import { shareBillToWhatsApp } from '../../utils/whatsappShareHelper';

// Styled components
const StyledAppBar = styled(AppBar)(({ theme }) => ({
  position: 'relative',
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  boxShadow: theme.shadows[1],
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

// Styled customer button
const CustomerButton = styled(Button)(({ theme }) => ({
  background: '#FF5722', // Bright orange color to stand out
  color: 'white',
  fontWeight: 'bold',
  padding: '10px 16px',
  borderRadius: '8px',
  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
  '&:hover': {
    background: '#E64A19',
    boxShadow: '0 6px 12px rgba(0,0,0,0.15)',
  },
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  transition: 'all 0.3s ease'
}));

/**
 * POS (Point of Sale) main component
 * @returns {JSX.Element} POS screen
 */
const formatOrderDateTime = (orderDate, createdAt) => {
  const datePart = (() => {
    if (!orderDate) return '';
    if (/^\d{7}$/.test(orderDate)) {
      const year = orderDate.substring(0, 4);
      const dayOfYear = parseInt(orderDate.substring(4), 10);
      const date = new Date(Number(year), 0);
      date.setDate(dayOfYear);
      return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
    }
    if (/^\d{6}$/.test(orderDate)) {
      const year = orderDate.substring(0, 4);
      const dayOfYear = parseInt(orderDate.substring(4), 10);
      const date = new Date(Number(year), 0);
      date.setDate(dayOfYear);
      return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
    }
    const parsedDate = new Date(orderDate);
    if (!isNaN(parsedDate.getTime())) {
      return `${parsedDate.getMonth() + 1}/${parsedDate.getDate()}/${parsedDate.getFullYear()}`;
    }
    return '';
  })();

  const timePart = (() => {
    if (!createdAt || !Array.isArray(createdAt) || createdAt.length < 6) return '';
    const [year, month, day, hour, minute, second] = createdAt;
    // Backend sends server time (often UTC in production); treat as UTC for correct local display
    const date = new Date(Date.UTC(year, month - 1, day, hour, minute, second || 0));
    if (!isNaN(date.getTime())) {
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    }
    return '';
  })();

  if (!datePart && !timePart) return '';
  if (!timePart) return datePart;
  return `${datePart} ${timePart}`;
};

const POS = () => {
  const [user, setUser] = useState(getCurrentUser());

  // Listen for company information updates
  useEffect(() => {
    const handleCompanyUpdate = () => {
      setUser(getCurrentUser());
    };

    window.addEventListener('companyInfoUpdated', handleCompanyUpdate);
    window.addEventListener('storage', handleCompanyUpdate);

    return () => {
      window.removeEventListener('companyInfoUpdated', handleCompanyUpdate);
      window.removeEventListener('storage', handleCompanyUpdate);
    };
  }, []);

  const theme = useTheme();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [rootCategories, setRootCategories] = useState([]);
  const [subcategories, setSubcategories] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(0); // 0 = All
  const [selectedParentCategory, setSelectedParentCategory] = useState(0); // 0 = All, used to track parent category
  const [displayedCategories, setDisplayedCategories] = useState([]); // Categories to be displayed in tabs
  const [cart, setCart] = useState(() => {
    // Initialize cart from localStorage if available
    const savedCart = localStorage.getItem('posCart');
    return savedCart ? JSON.parse(savedCart) : [];
  });
  const [cartOpen, setCartOpen] = useState(false);
  const [customer, setCustomer] = useState(() => {
    // Initialize customer from localStorage if available
    const savedCustomer = localStorage.getItem('posCustomer');
    return savedCustomer ? JSON.parse(savedCustomer) : null;
  });
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [currentSale, setCurrentSale] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [recentTransactionsOpen, setRecentTransactionsOpen] = useState(false);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [enableWhatsappShare, setEnableWhatsappShare] = useState(false);
  const [holdBillModalOpen, setHoldBillModalOpen] = useState(false);
  const [viewHoldBillsModalOpen, setViewHoldBillsModalOpen] = useState(false);

  // Discount and Additional Charges state
  const [discount, setDiscount] = useState(0);
  const [additionalCharges, setAdditionalCharges] = useState(0);

  // Location state
  const [userLocation, setUserLocation] = useState(null);
  const [isLocationUser, setIsLocationUser] = useState(false);

  // Quotation mode state
  const [isQuotationMode, setIsQuotationMode] = useState(false);
  const [quotationId, setQuotationId] = useState(null); // Store quotation ID when loading from View Orders

  // Cart panel width
  const cartWidth = 420; // Increased from 350

  // Mobile responsive state
  const isMobile = window.innerWidth < 960;

  // Handle navigation back to dashboard
  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  // Handle sale error
  const handleSaleError = (error) => {
    console.error('Sale error:', error);
    let msg = error.response?.data?.message || error.message || 'Failed to complete sale';
    const lower = String(msg || '').toLowerCase();
    if (lower.includes('day is not opened') || lower.includes('day not opened') || lower.includes('already closed')) {
      msg = 'Day is not open. Please open the day before saving the bill.';
    }
    alert(msg);
    setErrorMessage(null);
  };

  // Helper function to safely get customer credit balance
  const getCustomerCreditBalance = (customer) => {
    if (!customer) return '0.00';

    // Try different possible field names
    const creditValue = customer.creditBalance || customer.credit_balance || customer.credit || 0;
    return Number(creditValue).toFixed(2);
  };

  // Load products and categories on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Check if user has a location assigned
        let user = JSON.parse(localStorage.getItem('user')) || {};
        let userLocationData = user?.location || null;
        let hasLocation = userLocationData && userLocationData.id;

        // If location is missing, try to fetch current user data from backend
        if (!hasLocation) {
          console.log('[POS] Location not found in localStorage, fetching current user from backend...');
          try {
            const axios = (await import('../../utils/axios')).default;
            const response = await axios.get('/auth/me');
            if (response.data && response.data.location) {
              user = response.data;
              userLocationData = response.data.location;
              hasLocation = userLocationData && userLocationData.id;
              // Update localStorage with full user object
              localStorage.setItem('user', JSON.stringify(user));
              console.log('[POS] ✅ Fetched user location from backend:', userLocationData);
            }
          } catch (fetchError) {
            console.warn('[POS] Could not fetch user location from backend:', fetchError);
          }
        }

        setIsLocationUser(hasLocation);
        setUserLocation(userLocationData);

        if (hasLocation) {
          console.log('[POS] ✅ Location user detected:', userLocationData.name, '(ID:', userLocationData.id + ')');
        } else {
          console.log('[POS] ℹ️ Warehouse/Admin user - no location assigned');
        }

        let productsData;
        let activeProducts = [];

        if (hasLocation) {
          // Location user: Fetch products from location_products table
          console.log('[POS] Location user detected - fetching location products for location:', userLocationData.name);
          const locationProducts = await productService.getLocationProducts();

          // Convert location products to product format and filter by active status
          activeProducts = (locationProducts || [])
            .map(lp => ({
              id: lp.productId,
              name: lp.productName,
              code: lp.productCode,
              category: lp.categoryName,
              quantity: lp.quantity,
              sellingPrice: lp.sellingPrice || lp.purchasePrice || 0,
              purchasePrice: lp.purchasePrice || 0,
              minSalePrice: lp.minSalePrice || lp.sellingPrice || 0,
              imageUrl: lp.imageUrl,
              status: lp.status || 'ACTIVE',
              locationProductId: lp.id,
              locationQuantity: lp.quantity,
              locationId: lp.locationId,
              locationName: lp.locationName
            }))
            .filter(p => String(p.status || 'ACTIVE').toUpperCase() === 'ACTIVE');

          console.log('[POS] Loaded', activeProducts.length, 'active location products');
        } else {
          // Warehouse/Admin user: Fetch from products table
          console.log('[POS] Warehouse/Admin user - fetching all products');
          productsData = await productService.getAllProducts(0, 5000);

          // Filter out inactive products
          activeProducts = (productsData.content || []).filter(product =>
            String(product.status || 'ACTIVE').toUpperCase() === 'ACTIVE'
          );

          console.log('[POS] Loaded', activeProducts.length, 'warehouse products');
        }

        setProducts(activeProducts);
        setFilteredProducts(activeProducts); // Initialize filtered products

        // Load categories
        const categoriesData = await categoryService.getAllCategories({ page: 0, size: 1000 });

        // Process category hierarchy
        const categoryData = categoriesData.content || categoriesData;
        const { rootCategories: roots, subcategories: subs } = categoryService.buildCategoryTree({ content: categoryData });

        // Set root categories and subcategories
        setRootCategories(roots);
        setSubcategories(subs);

        // Display root categories with "All" option
        setDisplayedCategories([
          { id: 0, name: 'ALL' },
          ...roots
        ]);
      } catch (error) {
        console.error('Error loading POS data:', error);
        enqueueSnackbar('Failed to load products. Please try again.', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Load print settings (including WhatsApp share) for current location
  useEffect(() => {
    const loadSettings = async () => {
      try {
        // Get current user to determine location
        const userStr = localStorage.getItem('user');
        let locationId = null;
        if (userStr) {
          try {
            const user = JSON.parse(userStr);
            locationId = user.location?.id;
          } catch (e) {
            console.error('Error parsing user from localStorage', e);
          }
        }

        const settings = await printSettingsService.getPrintSettings(locationId);
        if (settings && typeof settings.enableWhatsappShare === 'boolean') {
          setEnableWhatsappShare(settings.enableWhatsappShare);
        }
      } catch (error) {
        console.error('Error loading print settings:', error);
        // Don't show error toast, just use default value
      }
    };

    loadSettings();
  }, []);

  // Load customers and set default "Walk-in Customer" on mount and when customer is null
  useEffect(() => {
    const loadCustomersAndSetDefault = async () => {
      try {
        if (!customer) {
          const response = await customerService.getAllCustomers({
            page: 0,
            size: 1000,
            sort: 'name',
            direction: 'ASC'
          });

          const customerList = response.content || (Array.isArray(response) ? response : []);

          const walkInCustomer = customerList.find(c =>
            (c.email && c.email.toLowerCase().includes('walkin')) ||
            (c.name && c.name.toLowerCase().includes('walk-in customer')) ||
            (c.customerId === '0000000000' || c.customerId === 0)
          );

          if (walkInCustomer) {
            setCustomer(walkInCustomer);
            localStorage.setItem('posCustomer', JSON.stringify(walkInCustomer));
            console.log('Default customer set to:', walkInCustomer.name);
          }
        }
      } catch (error) {
        console.error('Error loading customers for default selection:', error);
      }
    };

    loadCustomersAndSetDefault();
  }, [customer]);

  // Refresh customer data for latest loyalty points and balance when selected
  useEffect(() => {
    if (customer?.id && customer.id !== 1) {
      const fetchLatest = async () => {
        try {
          const fresh = await customerService.getCustomerById(customer.id);
          setCustomer(prev => {
            if (prev?.id === fresh.id) {
              return { ...prev, ...fresh };
            }
            return prev;
          });
        } catch (e) {
          console.error('Failed to fetch fresh customer data', e);
        }
      };
      fetchLatest();
    }
  }, [customer?.id]);



  // Filter products by category and search query
  useEffect(() => {
    let filtered = [...products].filter(product =>
      // First filter out inactive products
      String(product.status || 'ACTIVE').toUpperCase() === 'ACTIVE'
    );

    // Then filter by category
    // Note: For location products, category might be a string (categoryName) instead of object
    if (selectedCategory !== 0) {
      // Direct category filter - when user selects a specific category
      filtered = filtered.filter(product => {
        if (isLocationUser) {
          // Location products: category is a string (categoryName)
          // We need to find category by name
          const category = rootCategories.find(cat => cat.id === selectedCategory);
          return category && product.category === category.name;
        } else {
          // Warehouse products: category is an object
          return product.category && product.category.id === selectedCategory;
        }
      });
    } else if (selectedParentCategory !== 0) {
      // Parent category filter - when user is viewing "ALL IN CATEGORY"
      const subcategoryIds = subcategories[selectedParentCategory]?.map(sub => sub.id) || [];
      const parentCategory = rootCategories.find(cat => cat.id === selectedParentCategory);

      filtered = filtered.filter(product => {
        if (isLocationUser) {
          // Location products: category is a string
          if (parentCategory && product.category === parentCategory.name) return true;
          // Check subcategories
          return subcategories[selectedParentCategory]?.some(sub => product.category === sub.name);
        } else {
          // Warehouse products: category is an object
          return (product.category && product.category.id === selectedParentCategory) ||
            (product.category && subcategoryIds.includes(product.category.id));
        }
      });
    }

    // Finally filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(query) ||
        (product.barcode && product.barcode.toLowerCase().includes(query)) ||
        (product.sku && product.sku.toLowerCase().includes(query)) ||
        (product.code && product.code.toLowerCase().includes(query)) ||
        (product.description && product.description.toLowerCase().includes(query))
      );
    }

    setFilteredProducts(filtered);
  }, [products, selectedCategory, selectedParentCategory, searchQuery, subcategories, isLocationUser, rootCategories]);

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Handle category tab change
  const handleCategoryChange = (event, newValue) => {
    const newCategory = displayedCategories.find(cat => cat.id === newValue);

    if (newCategory) {
      // Set the selected category
      setSelectedCategory(newValue);

      // If it's the All category
      if (newValue === 0) {
        // If we're inside a parent category view
        if (selectedParentCategory !== 0) {
          // Keep the parent category context - just show all products in this category
          // The filtering will be handled in the useEffect
        } else {
          // Reset to root categories when top-level "All" is selected
          setDisplayedCategories([
            { id: 0, name: 'ALL' },
            ...rootCategories
          ]);
          setSelectedParentCategory(0);
        }
      } else {
        // Check if this category has subcategories
        const categoryHasSubcategories = subcategories[newValue] && subcategories[newValue].length > 0;

        if (categoryHasSubcategories) {
          // Show the subcategories of this parent
          setDisplayedCategories([
            { id: 0, name: 'ALL IN CATEGORY' },
            ...subcategories[newValue]
          ]);
          setSelectedParentCategory(newValue);
          setSelectedCategory(0); // Select "ALL IN CATEGORY" by default
        }
      }
    }
  };

  // Handle back to parent categories
  const handleBackToParentCategories = () => {
    setDisplayedCategories([
      { id: 0, name: 'ALL' },
      ...rootCategories
    ]);
    setSelectedParentCategory(0);
    setSelectedCategory(0);
  };

  // Add product to cart
  const handleAddToCart = (product) => {
    const existingItem = cart.find(item => item.product.id === product.id);

    // Map backend properties to frontend
    const price = product.sellingPrice || product.salePrice || 0;

    if (existingItem) {
      const updatedCart = cart.map(item =>
        item.product.id === product.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      );
      setCart(updatedCart);
    } else {
      setCart([...cart, {
        product,
        quantity: 1,
        price: price,
        tax: product.tax || null
      }]);
    }
  };

  // Remove product from cart
  const handleRemoveFromCart = (productId) => {
    const updatedCart = cart.filter(item => item.product.id !== productId);
    setCart(updatedCart);
  };

  // Update product quantity in cart
  const handleUpdateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      handleRemoveFromCart(productId);
      return;
    }

    const updatedCart = cart.map(item =>
      item.product.id === productId
        ? { ...item, quantity }
        : item
    );
    setCart(updatedCart);
  };

  // Update product unit price in cart (with min price enforcement already handled by POSCart)
  const handleUpdatePrice = (productId, newPrice) => {
    const updatedCart = cart.map(item =>
      item.product.id === productId
        ? { ...item, price: newPrice }
        : item
    );
    setCart(updatedCart);
  };

  // Clear all items from cart
  const handleClearCart = () => {
    setCart([]);
    setDiscount(0);
    setAdditionalCharges(0);
  };

  // Calculate cart totals
  const calculateTotals = () => {
    const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const taxAmount = cart.reduce((sum, item) => {
      const taxRate = item.tax ? item.tax.rate / 100 : 0;
      return sum + (item.price * item.quantity * taxRate);
    }, 0);

    const discountAmount = Math.max(0, parseFloat(discount) || 0);
    const additionalChargesAmount = Math.max(0, parseFloat(additionalCharges) || 0);

    // Ensure discount doesn't exceed subtotal + tax
    const maxDiscount = subtotal + taxAmount;
    const finalDiscount = Math.min(discountAmount, maxDiscount);

    const total = Math.max(0, subtotal + taxAmount - finalDiscount + additionalChargesAmount);

    return {
      subtotal,
      taxAmount,
      discount: finalDiscount,
      additionalCharges: additionalChargesAmount,
      total
    };
  };

  // Handle opening the payment dialog
  const handleOpenPayment = () => {
    if (cart.length === 0) return;

    // Clear previous sale state to ensure fresh payment form
    setCurrentSale(null);
    setSuccessMessage(null);
    setErrorMessage(null);

    setPaymentDialogOpen(true);
  };

  // Handle completing the sale
  const handleCompleteSale = async (paymentData) => {
    try {
      setProcessingPayment(true);
      setErrorMessage(null);

      // Format the date
      const today = new Date();
      const formattedDate = today.toISOString().split('T')[0];

      // Check payment method types
      const isCredit = paymentData.method === 'CREDIT';
      const isImmediatePayment = ['CASH', 'CREDIT_CARD', 'PAYNOW'].includes(paymentData.method);
      const isPendingPayment = ['CREDIT', 'CHEQUE'].includes(paymentData.method);
      const totalAmount = calculateTotals().total;

      // Validate credit sale
      if (isCredit) {
        if (!customer || !customer.id) {
          const errorMsg = "Customer selection is required for credit sales";
          alert(errorMsg);
          setErrorMessage(null);
          setProcessingPayment(false);
          return;
        }
      }

      // Determine payment status based on payment method
      const paymentStatus = isImmediatePayment ? "PAID" : (isPendingPayment ? "PENDING" : "PAID");
      console.log(`Payment method: ${paymentData.method}, Payment status: ${paymentStatus}`);

      // Get user's location ID from localStorage
      const user = JSON.parse(localStorage.getItem('user')) || {};
      const userLocationId = user?.location?.id || user?.locationId || null;

      // Prepare sales order data
      const salesOrder = {
        customerId: isCredit ? customer.id : (customer?.id || null),
        orderDate: formattedDate,
        companyId: user?.company?.id || user?.companyId || 1,
        items: cart.map(item => ({
          productId: item.product.id,
          quantity: item.quantity,
          unitPrice: item.price,
          taxId: item.tax ? item.tax.id : null,
          discount: 0
        })),
        paymentMethod: paymentData.method,
        paymentStatus: paymentStatus,
        status: isQuotationMode ? "PENDING" : "DELIVERED", // Quotations should be PENDING, not DELIVERED
        notes: paymentData.notes || '',
        subtotal: calculateTotals().subtotal,
        taxAmount: calculateTotals().taxAmount,
        total: totalAmount,
        discount: calculateTotals().discount,
        shippingCost: calculateTotals().additionalCharges,
        updateCreditBalance: false,
        locationId: userLocationId, // Send location ID for location-based sales
        isQuotation: isQuotationMode // Add quotation flag
      };

      console.log('[POS] Creating sales order with locationId:', userLocationId);

      // If this is a quotation being converted to bill, use convert endpoint
      let result;
      if (quotationId) {
        // Convert quotation to bill
        result = await salesService.convertQuotationToBill(quotationId, salesOrder);
        setQuotationId(null); // Clear quotation ID after conversion
      } else {
        // Create new sales order
        result = await salesService.createSalesOrder(salesOrder);
      }

      // NOTE: Credit balance is automatically handled by SalesOrderService
      // No need to create duplicate credit transactions here as it would cause double counting

      // Store current sale for receipt
      setCurrentSale(result);

      // Show success message
      const successMsg = isQuotationMode
        ? `Quotation #${result.id || 'New'} created successfully!`
        : `Sale #${result.id || 'New'} completed successfully!`;
      setSuccessMessage(successMsg);
      enqueueSnackbar(successMsg, {
        variant: 'success',
        autoHideDuration: 3000
      });

      // Reset quotation mode after successful creation
      if (isQuotationMode) {
        setIsQuotationMode(false);
      }

      // Clear the cart from localStorage
      localStorage.removeItem('posCart');
      // Don't clear posCustomer - setting customer to null will trigger useEffect to set default "Walk-in Customer"

      // Clear the state
      setCart([]);
      setCustomer(null); // This will trigger the useEffect to set default "Walk-in Customer"
      setDiscount(0);
      setAdditionalCharges(0);

    } catch (error) {
      console.error('Error completing sale:', error);
      handleSaleError(error);
    } finally {
      setProcessingPayment(false);
    }
  };

  // Handle WhatsApp share
  const handleShareWhatsApp = async () => {
    if (!currentSale?.id) return;

    try {
      await shareBillToWhatsApp(
        currentSale.id,
        customer,
        currentSale.orderNumber || currentSale.id
      );
    } catch (error) {
      console.error('Error sharing to WhatsApp:', error);
      enqueueSnackbar('Failed to share bill to WhatsApp', { variant: 'error' });
    }
  };

  // Handle printing receipt
  const handlePrintReceipt = async (receiptTypes = ['CUSTOMER']) => {
    if (!currentSale?.id) return;

    try {
      if (receiptTypes.length === 1) {
        // Single receipt type
        const type = receiptTypes[0];

        // Define custom header based on receipt type
        let customHeader;
        switch (type) {
          case 'CUSTOMER':
            customHeader = null; // Removed 'CUSTOMER COPY' - use default title
            break;
          case 'OFFICE':
            customHeader = null; // Removed 'OFFICE COPY' - use default title
            break;
          case 'OFFICE_WITH_SIGNATURE':
            customHeader = null; // Removed 'OFFICE COPY - CUSTOMER SIGNATURE REQUIRED' - use default title
            break;
          default:
            customHeader = null;
        }

        const receipt = await salesService.generateReceipt(currentSale.id, type, customHeader);

        // Create a blob URL and open it in a new tab
        const blob = new Blob([receipt], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');

        // Clean up the URL
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
      } else {
        // Multiple receipt types
        const receipts = await salesService.generateMultipleReceipts(currentSale.id, receiptTypes);

        // Generate display names for receipt types
        const getReceiptName = (type) => {
          switch (type) {
            case 'CUSTOMER': return 'Customer';
            case 'OFFICE': return 'Office';
            case 'OFFICE_WITH_SIGNATURE': return 'Office_with_Signature';
            default: return type;
          }
        };

        // Open each receipt in a new tab
        receipts.forEach(({ type, blob }) => {
          const fileName = `Invoice-${currentSale.id}-${getReceiptName(type)}.pdf`;
          const url = window.URL.createObjectURL(blob);

          // Create a hidden anchor element for downloading
          const downloadLink = document.createElement('a');
          downloadLink.href = url;
          downloadLink.download = fileName;
          downloadLink.target = '_blank';

          // Add to DOM, click, then remove
          document.body.appendChild(downloadLink);

          // Open for viewing in new tab and trigger download
          window.open(url, '_blank');
          downloadLink.click();
          document.body.removeChild(downloadLink);

          // Clean up the URL
          setTimeout(() => window.URL.revokeObjectURL(url), 100);
        });

        enqueueSnackbar(`${receipts.length} invoices generated successfully`, {
          variant: 'success',
          autoHideDuration: 3000
        });
      }
    } catch (error) {
      console.error('Error generating receipt(s):', error);
      enqueueSnackbar('Failed to generate receipt(s). Please try again.', {
        variant: 'error'
      });
    }
  };

  // Handle error close
  const handleErrorClose = () => {
    setErrorMessage(null);
  };

  // Handle success close
  const handleSuccessClose = () => {
    setSuccessMessage(null);
  };

  // Fetch recent transactions
  const fetchRecentTransactions = async () => {
    try {
      setLoadingTransactions(true);
      const response = await salesService.getAllSalesOrders({ page: 0, size: 3, sort: 'createdAt', direction: 'DESC' });
      setRecentTransactions(response.content || []);
    } catch (error) {
      console.error('Error fetching recent transactions:', error);
      enqueueSnackbar('Failed to load recent transactions', { variant: 'error' });
    } finally {
      setLoadingTransactions(false);
    }
  };

  // Handle opening recent transactions
  const handleOpenRecentTransactions = () => {
    setRecentTransactionsOpen(true);
    fetchRecentTransactions();
  };

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('posCart', JSON.stringify(cart));
  }, [cart]);

  // Save customer to localStorage whenever it changes
  // Don't remove from localStorage when customer is null - this allows default to be set on refresh
  useEffect(() => {
    if (customer) {
      localStorage.setItem('posCustomer', JSON.stringify(customer));
    }
    // Don't remove from localStorage when null - let the default loading handle it
  }, [customer]);

  // Load quotation data when navigating from View Orders
  useEffect(() => {
    const loadQuotation = async () => {
      try {
        const quotationDataStr = localStorage.getItem('quotationToLoad');
        if (!quotationDataStr) return;

        const quotationData = JSON.parse(quotationDataStr);

        // Clear the quotation data from localStorage
        localStorage.removeItem('quotationToLoad');

        // If quotation doesn't have items, fetch them
        let quotation = quotationData;
        if (!quotation.items || quotation.items.length === 0) {
          quotation = await salesService.getSalesOrderById(quotation.id);
        }

        // Set customer
        if (quotation.customer) {
          setCustomer(quotation.customer);
        }

        // Load items into cart
        if (quotation.items && quotation.items.length > 0) {
          const cartItems = quotation.items.map(item => {
            // Build product object
            const product = item.product || {
              id: item.productId,
              name: item.productName || 'Unknown Product',
              salePrice: item.unitPrice,
              sellingPrice: item.unitPrice
            };

            return {
              product: product,
              quantity: item.quantity,
              price: item.unitPrice,
              tax: item.tax || null,
              taxAmount: item.taxAmount || 0,
              discount: item.discount || 0
            };
          });

          setCart(cartItems);

          // Set discount and additional charges if present
          if (quotation.discount) {
            setDiscount(Number(quotation.discount));
          }
          if (quotation.shippingCost) {
            setAdditionalCharges(Number(quotation.shippingCost));
          }
        }

        // Store quotation ID for reference (will be used when saving)
        setQuotationId(quotation.id);

        // Set quotation mode to false (we're converting to bill)
        setIsQuotationMode(false);

        enqueueSnackbar('Quotation loaded. You can edit items and proceed to bill.', {
          variant: 'info',
          autoHideDuration: 4000
        });
      } catch (error) {
        console.error('Error loading quotation:', error);
        enqueueSnackbar('Failed to load quotation data', { variant: 'error' });
      }
    };

    loadQuotation();
  }, []); // Run only once on mount

  // Handle hold bill success
  const handleHoldBillSuccess = () => {
    enqueueSnackbar('Bill held successfully', { variant: 'success' });
    handleClearCart();
    setCustomer(null);
    localStorage.removeItem('posCustomer');
  };

  // Handle processing a hold bill
  const handleProcessHoldBill = async (bill) => {
    try {
      // Load items into cart
      setCart(bill.items.map(item => ({
        product: {
          id: item.productId,
          name: item.productName,
          salePrice: item.unitPrice
        },
        quantity: item.quantity,
        price: item.unitPrice,
        tax: item.tax || null,
        taxAmount: item.taxAmount
      })));

      // Set customer
      setCustomer({
        id: bill.customerId,
        name: bill.customerName
      });

      // Delete the hold bill since it's now in the cart
      await holdBillService.deleteHoldBill(bill.id);

      enqueueSnackbar('Hold bill loaded successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error processing hold bill:', error);
      enqueueSnackbar('Failed to process hold bill', { variant: 'error' });
    }
  };

  // Add a function to manually clear the draft
  const handleClearDraft = () => {
    localStorage.removeItem('posCart');
    localStorage.removeItem('posCustomer');
    setCart([]);
    setCustomer(null);
    setDiscount(0);
    setAdditionalCharges(0);
    enqueueSnackbar('Draft cleared successfully', { variant: 'success' });
  };

  return (
    <Box sx={{
      width: '100%',
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Top AppBar */}
      <StyledAppBar position="static" color="default" elevation={0}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleBackToDashboard}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexGrow: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              POS
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar
                src={user.logoUrl ? `${user.logoUrl}${user.logoUrl.includes('?') ? '&' : '?'}_t=${Date.now()}` : ''}
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: 'white',
                  border: '1px solid rgba(0,0,0,0.1)'
                }}
              >
                {!user.logoUrl && <BusinessIcon color="primary" sx={{ fontSize: 18 }} />}
              </Avatar>
              <Typography
                variant="h6"
                noWrap
                component="div"
                sx={{ fontWeight: 'bold' }}
              >
                {user.companyName || 'Tiya Inventory'}
              </Typography>
            </Box>
          </Box>
          {/* Display location if user is a location user */}
          {isLocationUser && userLocation && (
            <Chip
              icon={<LocationOnIcon />}
              label={`Location: ${userLocation.name || 'N/A'}`}
              color="primary"
              variant="outlined"
              sx={{
                mr: 2,
                fontWeight: 'bold',
                fontSize: '0.875rem'
              }}
            />
          )}

          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
              <TextField
                placeholder="Search products..."
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={handleSearchChange}
                sx={{ width: 300, mr: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="outlined"
                startIcon={<HistoryIcon />}
                onClick={handleOpenRecentTransactions}
                sx={{ mr: 2 }}
              >
                Recent Transactions
              </Button>
              <Button
                variant="outlined"
                startIcon={<ListIcon />}
                onClick={() => setViewHoldBillsModalOpen(true)}
                sx={{ mr: 2 }}
              >
                View Hold Bills
              </Button>
              <Button
                variant="outlined"
                startIcon={<SaveIcon />}
                onClick={() => setHoldBillModalOpen(true)}
                disabled={cart.length === 0}
                sx={{ mr: 2 }}
              >
                Hold Bill
              </Button>
              {(cart.length > 0 || customer) && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleClearDraft}
                  sx={{ mr: 2 }}
                >
                  Clear Draft
                </Button>
              )}
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mr: 2 }}>
            <CustomerButton
              startIcon={<PersonIcon sx={{ fontSize: 20 }} />}
              onClick={() => setCustomerDialogOpen(true)}
              fullWidth
            >
              {customer ? customer.name : 'Select Customer'}
            </CustomerButton>

            {customer && customer.id !== 1 && (
              <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5 }}>
                <Typography variant="caption" sx={{ color: '#1565c0', fontWeight: 'bold' }}>
                  Points: {customer.loyaltyPoints ?? 0}
                </Typography>
                <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                  Due Balance: {getCustomerCreditBalance(customer)}
                </Typography>
              </Box>
            )}
          </Box>

          <IconButton
            color="primary"
            onClick={() => setCartOpen(true)}
            sx={{ display: { xs: 'inline-flex', md: 'none' } }}
          >
            <Badge badgeContent={cart.length} color="error">
              <CartIcon />
            </Badge>
          </IconButton>
        </Toolbar>

        {/* Big Customer Select Button for better visibility - show on mobile and tablet */}
        {(isMobile) && (
          <Box sx={{
            p: 2,
            display: { xs: 'block', md: 'none' },
            borderTop: 1,
            borderColor: 'divider'
          }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <CustomerButton
                  startIcon={<PersonIcon />}
                  onClick={() => setCustomerDialogOpen(true)}
                  fullWidth
                  size="large"
                >
                  {customer ? customer.name : 'Select Customer'}
                </CustomerButton>
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={4}>
                <Button
                  variant="outlined"
                  startIcon={<ListIcon />}
                  onClick={() => setViewHoldBillsModalOpen(true)}
                  fullWidth
                  size="large"
                >
                  View Hold Bills
                </Button>
              </Grid>
              <Grid item xs={4}>
                <Button
                  variant="outlined"
                  startIcon={<SaveIcon />}
                  onClick={() => setHoldBillModalOpen(true)}
                  disabled={cart.length === 0}
                  fullWidth
                  size="large"
                >
                  Hold Bill
                </Button>
              </Grid>
              <Grid item xs={4}>
                {(cart.length > 0 || customer) && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleClearDraft}
                    fullWidth
                    size="large"
                  >
                    Clear Draft
                  </Button>
                )}
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Category navigation */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
          px: 2
        }}>
          {selectedParentCategory !== 0 && (
            <Button
              onClick={handleBackToParentCategories}
              startIcon={<ArrowBackIcon />}
              size="small"
              sx={{ mr: 2 }}
            >
              Back
            </Button>
          )}
          <Tabs
            value={selectedCategory}
            onChange={handleCategoryChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ flexGrow: 1 }}
          >
            {displayedCategories.map(category => (
              <Tab key={category.id} label={category.name} value={category.id} />
            ))}
          </Tabs>
        </Box>

        {isMobile && (
          <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider' }}>
            <TextField
              placeholder="Search products..."
              variant="outlined"
              size="small"
              fullWidth
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )}
      </StyledAppBar>

      {/* Main content */}
      <Box sx={{
        display: 'flex',
        flexGrow: 1,
        overflow: 'hidden',
        position: 'relative'
      }}>
        {/* Products grid - shown in both desktop and mobile */}
        <Box sx={{
          flexGrow: 1,
          overflow: 'auto',
          pr: { md: `${cartWidth}px` },
          width: '100%',
          height: '100%',
          pt: 2,
          // Add custom scrollbar styles to the Box container as well
          '&::-webkit-scrollbar': {
            width: '10px',
            backgroundColor: 'rgba(0, 0, 0, 0.05)'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '5px',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.3)'
            }
          },
          // For Firefox
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(0, 0, 0, 0.2) rgba(0, 0, 0, 0.05)'
        }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <POSProductList
              products={filteredProducts}
              onAddToCart={handleAddToCart}
            />
          )}
        </Box>

        {/* Cart panel - only visible on desktop */}
        {!isMobile && (
          <Box sx={{
            width: cartWidth,
            flexShrink: 0,
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            borderLeft: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            zIndex: 10,
            overflow: 'hidden',
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column'
          }}>
            {/* Quotation Mode Toggle */}
            <Box sx={{
              p: 1.5,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: isQuotationMode ? 'warning.light' : 'background.paper',
              transition: 'background-color 0.3s'
            }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isQuotationMode}
                    onChange={(e) => setIsQuotationMode(e.target.checked)}
                    color="warning"
                    size="small"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <DescriptionIcon fontSize="small" />
                    <Typography variant="body2" fontWeight={isQuotationMode ? 'bold' : 'normal'}>
                      Quotation Bill
                    </Typography>
                  </Box>
                }
                sx={{ m: 0 }}
              />
              {isQuotationMode && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block', mt: 0.5 }}>
                  Stock will not be deducted
                </Typography>
              )}
            </Box>
            <POSCart
              cartItems={cart}
              onUpdateQuantity={handleUpdateQuantity}
              onRemoveItem={handleRemoveFromCart}
              onCheckout={handleOpenPayment}
              onClearCart={handleClearCart}
              onUpdatePrice={handleUpdatePrice}
              totals={calculateTotals()}
              discount={discount}
              onDiscountChange={setDiscount}
              additionalCharges={additionalCharges}
              onAdditionalChargesChange={setAdditionalCharges}
            />
          </Box>
        )}
      </Box>

      {/* Mobile Cart Drawer */}
      <Drawer
        anchor="right"
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        sx={{ display: { xs: 'block', md: 'none' } }}
        PaperProps={{ sx: { width: '90%', maxWidth: cartWidth, height: '100%', display: 'flex', flexDirection: 'column' } }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">
            Shopping Cart
          </Typography>
          <IconButton onClick={() => setCartOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        {/* Quotation Mode Toggle for Mobile */}
        <Box sx={{
          p: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: isQuotationMode ? 'warning.light' : 'background.paper',
          transition: 'background-color 0.3s'
        }}>
          <FormControlLabel
            control={
              <Switch
                checked={isQuotationMode}
                onChange={(e) => setIsQuotationMode(e.target.checked)}
                color="warning"
                size="small"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <DescriptionIcon fontSize="small" />
                <Typography variant="body2" fontWeight={isQuotationMode ? 'bold' : 'normal'}>
                  Quotation Bill
                </Typography>
              </Box>
            }
            sx={{ m: 0 }}
          />
          {isQuotationMode && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block', mt: 0.5 }}>
              Stock will not be deducted
            </Typography>
          )}
        </Box>
        <POSCart
          cartItems={cart}
          onUpdateQuantity={handleUpdateQuantity}
          onRemoveItem={handleRemoveFromCart}
          onCheckout={() => {
            setCartOpen(false);
            handleOpenPayment();
          }}
          onClearCart={handleClearCart}
          onUpdatePrice={handleUpdatePrice}
          totals={calculateTotals()}
          discount={discount}
          onDiscountChange={setDiscount}
          additionalCharges={additionalCharges}
          onAdditionalChargesChange={setAdditionalCharges}
        />
      </Drawer>

      {/* Customer Dialog */}
      <Dialog
        open={customerDialogOpen}
        onClose={() => setCustomerDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Select Customer
          <IconButton
            aria-label="close"
            onClick={() => setCustomerDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <CustomerSelector
            selectedCustomer={customer}
            onSelectCustomer={(selectedCustomer) => {
              setCustomer(selectedCustomer);
              setCustomerDialogOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Recent Transactions Dialog */}
      <Dialog
        open={recentTransactionsOpen}
        onClose={() => setRecentTransactionsOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Recent Transactions
          <IconButton
            aria-label="close"
            onClick={() => setRecentTransactionsOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loadingTransactions ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              {recentTransactions.length === 0 ? (
                <Typography align="center" color="textSecondary" sx={{ py: 3 }}>
                  No recent transactions found
                </Typography>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Order #</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Customer</TableCell>
                        <TableCell>Items</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentTransactions.map(transaction => (
                        <TableRow key={transaction.id}>
                          <TableCell>{transaction.orderNumber || transaction.id}</TableCell>
                          <TableCell>{formatOrderDateTime(transaction.orderDate, transaction.createdAt)}</TableCell>
                          <TableCell>{transaction.customer?.name || 'Walk-in Customer'}</TableCell>
                          <TableCell>{transaction.items?.length || 0} items</TableCell>
                          <TableCell align="right">₹{transaction.total?.toFixed(2)}</TableCell>
                          <TableCell>
                            <Chip
                              label={transaction.status || 'Completed'}
                              color={transaction.status === 'PAID' ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog
        open={paymentDialogOpen}
        onClose={() => !processingPayment && setPaymentDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Complete Payment
          <IconButton
            aria-label="close"
            onClick={() => !processingPayment && setPaymentDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
            disabled={processingPayment}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {errorMessage && (
            <Alert
              severity="error"
              onClose={handleErrorClose}
              sx={{ mb: 2 }}
            >
              {errorMessage}
            </Alert>
          )}

          {successMessage && (
            <Alert
              severity="success"
              onClose={handleSuccessClose}
              sx={{ mb: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() => handlePrintReceipt(['CUSTOMER'])}
                >
                  Print Receipt
                </Button>
              }
            >
              {successMessage}
            </Alert>
          )}

          <POSPayment
            totals={calculateTotals()}
            onCompletePayment={handleCompleteSale}
            customer={customer}
            loading={processingPayment}
            saleCompleted={!!currentSale}
            onPrintReceipt={handlePrintReceipt}
            enableWhatsappShare={enableWhatsappShare}
            onShareWhatsApp={handleShareWhatsApp}
          />
        </DialogContent>
      </Dialog>

      {/* Hold Bill Modal */}
      <HoldBillModal
        open={holdBillModalOpen}
        onClose={() => setHoldBillModalOpen(false)}
        customer={customer}
        cartItems={cart}
        onSuccess={handleHoldBillSuccess}
      />

      {/* View Hold Bills Modal */}
      <ViewHoldBillsModal
        open={viewHoldBillsModalOpen}
        onClose={() => setViewHoldBillsModalOpen(false)}
        onProcessBill={handleProcessHoldBill}
      />
    </Box>
  );
};

export default POS; 
